import { makeErrorField } from '../../../../helpers/utils/form.utils';

/**
 * Mapper puro DTO real (`buscarDadosDevedor`) → shape de UI (AW2-1134 Etapas
 * 2-3) — porta de `dadosDevedorController.makeFormFields`/`buildEnderecoDevedor`
 * reais sem `FieldModel` (protótipo usa `useState` plano, mesmo padrão já
 * usado em todo o módulo — ver `BlocoAnotacao.jsx`).
 *
 * `categoria` (não `idCategoria`) é o campo correto — ver JSDoc de
 * `IdentificacaoDevedor.jsx` e `src/prototipo/CLAUDE.md` (achado confirmado
 * ao vivo na Etapa 1: `idCategoria` é um id numérico interno sem relação com
 * o selector `categorias`, que usa a string enum de `categoria`).
 */
const CAMPOS_IDENTIFICACAO_DEVEDOR = [
  'categoria',
  'cnpjCpf',
  'dataFundacao',
  'situacaoCnpj',
  'codigoNoCliente',
  'idDevedor',
  'razaoSocial',
  'preposto',
  'nomeFantasia',
  'inscricaoEstadual',
];

/**
 * Dados do Advogado do devedor (AW2-1134 Etapa 6) — 2 campos simples,
 * lidos direto do DTO sem transformação (nomes já batem com
 * `makeFormFields`/`makePayload` reais). Array separado de
 * `CAMPOS_IDENTIFICACAO_DEVEDOR` só por clareza — reflete o componente
 * próprio (`DadosAdvogado.jsx`) que a produção real também usa, mas
 * funcionalmente é achatado no mesmo `formFields` plano, como todo o resto.
 */
const CAMPOS_ADVOGADO = ['nomeAdvogado', 'telefoneAdvogado'];

/**
 * "Última pesquisa"/"Retorno consulta" (AW2-1134 Etapa 10) — resultado da
 * consulta ao Webservice externo (`constultarDadosComplementaresDevedorWebserviceExterno`),
 * nomes já batem entre DTO e form real (`dataUltimaConsulta: FieldModel.newInstance(values?.dataUltimaConsulta || '')`).
 * Sempre somente-leitura (mesmo em modo edição, produção real não passa
 * `disabled` condicional para esses 2 campos) — populados só por refetch
 * pós-consulta, nunca digitados pelo usuário.
 */
const CAMPOS_CONSULTA_WEBSERVICE = ['dataUltimaConsulta', 'retornoConsulta'];

const CAMPOS_ENDERECO = [
  'idDevedorEndereco',
  'idEndereco',
  'estado',
  'endereco',
  'numero',
  'complemento',
  'bairro',
  'cidade',
  'cep',
];

/**
 * Item vazio para "Adicionar Endereço" — `principal: false` por padrão,
 * mesmo default de `buildEndereco()` real (`helpers/factory/endereco.factory.js`,
 * chamado sem args pelo handler de adicionar) — nunca demove o principal
 * existente silenciosamente ao adicionar um novo item.
 */
export function buildEnderecoVazio() {
  return CAMPOS_ENDERECO.reduce((campos, campo) => ({ ...campos, [campo]: '' }), { principal: false });
}

/**
 * Ordena com o principal primeiro — mesmo critério de
 * `buildEnderecosDevedor` real (`.sort((origin, to) => to.principal - origin.principal)`).
 */
function mapearEnderecos(enderecos) {
  return (enderecos || [])
    .slice()
    .sort((origem, destino) => Number(destino?.principal) - Number(origem?.principal))
    .map(endereco => CAMPOS_ENDERECO.reduce(
      (campos, campo) => ({ ...campos, [campo]: endereco?.[campo] ?? '' }),
      { principal: endereco?.principal ?? false },
    ));
}

const CAMPOS_TELEFONE = [
  'idTelefone',
  'idEntidadeTelefone',
  'tipoTelefone',
  'ddd',
  'numero',
  'observacao',
];

/**
 * Item vazio para "Adicionar Telefone" — `principal`/`permitirWhatsApp`
 * `false` por padrão, mesmo default de `buildTelefone()` real
 * (`helpers/factory/telefone.factory.js`, chamado sem args). Quem decide se
 * o novo item deve nascer `principal: true` (lista antes vazia) é o handler
 * de adicionar em `AbaDevedor.jsx` — mesmo padrão já usado para
 * `buildEnderecoVazio`/`onAdicionarEndereco` na Etapa 3, e confirmado desta
 * vez por um caminho de produção genuinamente alcançável (`Telefones.jsx`
 * real, `addTelefoneHandler`: `if (modifiedTelefones.length === 0) {
 * telefone.principal.onChange(true); }` — não código morto, ao contrário do
 * achado equivalente em `buildEnderecosDevedor`).
 */
export function buildTelefoneVazio() {
  return CAMPOS_TELEFONE.reduce(
    (campos, campo) => ({ ...campos, [campo]: '' }),
    { principal: false, permitirWhatsApp: false },
  );
}

/**
 * Ordena com o principal primeiro — mesmo critério de `buildTelefones` real.
 * Diferente de Endereços, `permitirWhatsApp` NÃO é mutuamente exclusivo para
 * o devedor — `TelefoneDevedor.jsx` real não passa `validaWhatsApp` (default
 * `false` em `Telefones.jsx`), então cada telefone liga/desliga WhatsApp
 * independente dos demais.
 */
function mapearTelefones(telefones) {
  return (telefones || [])
    .slice()
    .sort((origem, destino) => Number(destino?.principal) - Number(origem?.principal))
    .map(telefone => CAMPOS_TELEFONE.reduce(
      (campos, campo) => ({ ...campos, [campo]: telefone?.[campo] ?? '' }),
      { principal: telefone?.principal ?? false, permitirWhatsApp: telefone?.permitirWhatsApp ?? false },
    ));
}

/**
 * `idDevedorEmail` (não `idBorderoEmail`) é o campo correto — confirmado por
 * leitura direta de `dadosDevedorController.buildEmailDevedor` real. O
 * factory genérico de e-mail (`helpers/factory/email.factory.js`,
 * `buildSimpleEmail`, usado pelo `addEmailHandler` de `SimpleEmails.jsx`
 * real ao criar um item novo) usa `idBorderoEmail` — nome de um contexto
 * diferente (Bordero), reaproveitado ali sem relação com Devedor. Como um
 * item novo nunca tem id mesmo (é gerado pelo backend na criação), essa
 * divergência de nome de campo entre "mapear item existente" e "criar item
 * novo" na produção real é inofensiva — aqui usamos `idDevedorEmail` nos
 * dois casos por consistência interna, sem replicar a inconsistência.
 *
 * ACHADO REAL (não hipotético, confirmado por leitura direta, code review):
 * `buildEmailDevedor` real tem um typo na chave lida do DTO —
 * `getAttributeExistingObject(devedorEmailCommand, 'emidDevedorEmailail')`
 * (deveria ser `'idDevedorEmail'`). Isso significa que, em PRODUÇÃO,
 * `idDevedorEmail.value` NUNCA é populado para um e-mail já existente — e
 * `makeDevedorEmailCommand` real (`email?.idDevedorEmail?.value || null`)
 * sempre envia `idDevedorEmail: null` no payload de update, mesmo para
 * e-mails que já têm id. `mapearEmails` abaixo, ao contrário, lê o campo
 * `idDevedorEmail` correto direto do DTO — a divergência (protótipo
 * preservaria o id onde produção sempre perde) foi levantada explicitamente
 * com o usuário na Etapa 9: **decisão confirmada — replicar o bug real**.
 * `montarEmailCommand` abaixo força `idDevedorEmail: null` sempre no
 * payload de envio, por decisão de paridade exata (não por engano — o
 * mapper de leitura acima continua correto, só o envio replica o defeito).
 */
const CAMPOS_EMAIL = ['idDevedorEmail', 'email'];

/**
 * Item vazio para "Adicionar E-mail" — `principal: false` por padrão, mesmo
 * critério de `buildSimpleEmail()` real chamado sem args pelo handler de
 * adicionar (`addEmailHandler`, `SimpleEmails.jsx`).
 */
export function buildEmailVazio() {
  return CAMPOS_EMAIL.reduce((campos, campo) => ({ ...campos, [campo]: '' }), { principal: false });
}

/**
 * Ordena com o principal primeiro, mesmo critério dos demais mappers desta
 * feature — `SimpleEmails.jsx` real não ordena a lista (`buildEmailComponent`
 * itera na ordem recebida), mas seguir o mesmo critério de exibição de
 * Endereços/Telefones é consistente com o resto da aba e não muda nenhum
 * comportamento de negócio (a ordem de exibição não é enviada ao backend).
 */
function mapearEmails(emails) {
  return (emails || [])
    .slice()
    .sort((origem, destino) => Number(destino?.principal) - Number(origem?.principal))
    .map(email => CAMPOS_EMAIL.reduce(
      (campos, campo) => ({ ...campos, [campo]: email?.[campo] ?? '' }),
      { principal: email?.principal ?? false },
    ));
}

const CAMPOS_SOCIO = ['idSocio', 'nome', 'cpf'];

/**
 * Sócios/QSA (AW2-1134 Etapa 7) — sem ordenação por `principal` (não existe
 * esse conceito aqui, diferente de Endereços/Telefones/E-mails) e sem
 * `buildSocioVazio`/handlers de add-remove — bloco somente-leitura, sem
 * "Adicionar Sócio" na produção real (`DadosSocios.jsx` real não tem nenhum
 * botão de edição, só exibe `formFields.socios`).
 */
function mapearSocios(socios) {
  return (socios || []).map(socio => CAMPOS_SOCIO.reduce(
    (campos, campo) => ({ ...campos, [campo]: socio?.[campo] ?? '' }),
    {},
  ));
}

export function mapearDevedorParaFormFields(dadosDevedor) {
  return {
    ...CAMPOS_IDENTIFICACAO_DEVEDOR.reduce(
      (campos, campo) => ({ ...campos, [campo]: dadosDevedor?.[campo] ?? '' }),
      {},
    ),
    ...CAMPOS_ADVOGADO.reduce(
      (campos, campo) => ({ ...campos, [campo]: dadosDevedor?.[campo] ?? '' }),
      {},
    ),
    ...CAMPOS_CONSULTA_WEBSERVICE.reduce(
      (campos, campo) => ({ ...campos, [campo]: dadosDevedor?.[campo] ?? '' }),
      {},
    ),
    // `codDevedorPrincipal` (chave do form) <- `idDevedorRelacionado` (chave
    // do DTO) — nomes divergentes confirmados por leitura direta de
    // `dadosDevedorController.makeFormFields` real
    // (`codDevedorPrincipal: FieldModel.newInstance(values?.idDevedorRelacionado || '')`),
    // não um erro de digitação a "corrigir" aqui.
    codDevedorPrincipal: dadosDevedor?.idDevedorRelacionado ?? '',
    enderecos: mapearEnderecos(dadosDevedor?.enderecos),
    telefones: mapearTelefones(dadosDevedor?.telefones),
    emails: mapearEmails(dadosDevedor?.emails),
    socios: mapearSocios(dadosDevedor?.socios),
  };
}

function montarEnderecoCommand(endereco) {
  return {
    bairro: endereco.bairro || null,
    cep: endereco.cep || null,
    cidade: endereco.cidade || null,
    complemento: endereco.complemento || null,
    endereco: endereco.endereco || null,
    estado: endereco.estado || null,
    idEndereco: endereco.idEndereco || null,
    numero: endereco.numero || null,
    idDevedorEndereco: endereco.idDevedorEndereco || null,
    principal: endereco.principal,
  };
}

function montarTelefoneCommand(telefone) {
  return {
    ddd: telefone.ddd || null,
    idEntidadeTelefone: telefone.idEntidadeTelefone || null,
    idTelefone: telefone.idTelefone || null,
    numero: telefone.numero || null,
    observacao: telefone.observacao || null,
    permitirWhatsApp: telefone.permitirWhatsApp,
    principal: telefone.principal,
    tipoTelefone: telefone.tipoTelefone || null,
  };
}

/**
 * `idDevedorEmail` sempre `null` — decisão de paridade exata com o bug real
 * de `makeDevedorEmailCommand` (ver JSDoc de `CAMPOS_EMAIL` acima),
 * confirmada explicitamente com o usuário na Etapa 9. Não usar
 * `email.idDevedorEmail` aqui mesmo ele estando disponível e correto em
 * `formFields` — o objetivo é reproduzir o comportamento observável da
 * produção (perde o id de e-mails existentes a cada save), não corrigi-lo.
 */
function montarEmailCommand(email) {
  return {
    email: email.email || null,
    idDevedorEmail: null,
    principal: email.principal,
  };
}

/**
 * Porta de `dadosDevedorController.makePayload` real. `idCategoria` (chave
 * do payload) recebe `formFields.categoria` (chave do form) — mesma
 * confusão de nomenclatura já documentada em `CAMPOS_IDENTIFICACAO_DEVEDOR`
 * acima (produção também faz `idCategoria: fields?.idCategoria?.value`,
 * onde `idCategoria` é o NOME do campo do form que na real guarda a STRING
 * `categoria`, não o id numérico) — fidelidade ao real, não erro.
 *
 * `nomePreposto` (chave do payload) <- `formFields.preposto` (chave do
 * form) — mesma divergência de nome já usada por `makeErrorFields` real
 * (`nomePreposto: makeErrorField()`), relevante para o mapeamento de erro
 * de validação por campo (ver `AbaDevedor.jsx`).
 *
 * `permiteCnpj` deriva de `categoriasOptions` (selector real, `{id, value,
 * aux}`) — `aux === 'CNPJ'` é o mesmo critério de
 * `immutableSelectors.categorias.filter(...)[0].aux === 'CNPJ'` real.
 */
export function montarPayloadDevedor(formFields, categoriasOptions, idProcesso) {
  const categoriaSelecionada = (categoriasOptions || []).find(cat => cat.id === formFields.categoria);

  return {
    cnpjCpf: formFields.cnpjCpf,
    codigoNoCliente: formFields.codigoNoCliente,
    idCategoria: formFields.categoria,
    idDevedor: formFields.idDevedor,
    idProcesso,
    inscricaoEstadual: formFields.inscricaoEstadual,
    nomeAdvogado: formFields.nomeAdvogado,
    telefoneAdvogado: formFields.telefoneAdvogado,
    nomeFantasia: formFields.nomeFantasia,
    razaoSocial: formFields.razaoSocial,
    nomePreposto: formFields.preposto,
    permiteCnpj: categoriaSelecionada?.aux === 'CNPJ',
    enderecos: (formFields.enderecos || []).map(montarEnderecoCommand),
    telefones: (formFields.telefones || []).map(montarTelefoneCommand),
    emails: (formFields.emails || []).map(montarEmailCommand),
    idDevedorRelacionado: formFields.codDevedorPrincipal,
  };
}

/**
 * `errorFields` iniciais para o submit real (Etapa 9) — só os campos
 * simples/flat (Identificação + Advogado) que a UI consegue exibir com
 * `error`/`helperText` num `Field` único. Chaves batem com o PAYLOAD (não
 * com `formFields`) porque é isso que o backend usa no path de validação —
 * único desvio é `nomePreposto` (payload) vs. `preposto` (UI), mapeado na
 * apresentação (`AbaDevedor.jsx`).
 *
 * `enderecos`/`telefones`/`emails`/`idCategoria`/`idDevedor`/`idProcesso`
 * ficam DE FORA de propósito — são arrays (validação indexada, ex.
 * `enderecos[0].cep`, quebraria `updateFieldsWithErrors`/`setErrorsInFields`
 * real ao tentar indexar um objeto plano `{error, errorMessage}` como se
 * fosse array) ou campos sem edição direta nesta etapa. Qualquer validação
 * fora dessas chaves cai no bucket "não mapeados" tratado em
 * `AbaDevedor.jsx` (mesmo padrão de fallback já usado em
 * `DialogAnaliseSimulacao.jsx`).
 */
export function makeErrorFieldsDevedor() {
  return {
    cnpjCpf: makeErrorField(),
    codigoNoCliente: makeErrorField(),
    inscricaoEstadual: makeErrorField(),
    nomeAdvogado: makeErrorField(),
    telefoneAdvogado: makeErrorField(),
    nomeFantasia: makeErrorField(),
    razaoSocial: makeErrorField(),
    nomePreposto: makeErrorField(),
  };
}
